import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Search, FileText, Download, Eye, Calendar, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '@/utils/exportExcel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminEmailFilter, setAdminEmailFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchLogs();
  }, [skip, actionFilter, entityFilter]);

  const fetchLogs = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip, limit });
      if (adminEmailFilter) params.append('admin_email', adminEmailFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (entityFilter) params.append('entity_type', entityFilter);
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) params.append('end_date', new Date(endDate).toISOString());

      const response = await axios.get(`${API}/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load audit logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSkip(0);
    fetchLogs();
  };

  const handleExportExcel = () => {
    const formattedLogs = logs.map(log => ({
      'Timestamp': new Date(log.timestamp).toLocaleString(),
      'Admin Email': log.admin_email,
      'Action': log.action.toUpperCase(),
      'Entity Type': log.entity_type,
      'Entity Name': log.entity_name || 'N/A',
      'Entity ID': log.entity_id,
      'Reason': log.reason || 'N/A',
      'IP Address': log.ip_address || 'N/A'
    }));
    
    exportToExcel(formattedLogs, `audit_logs_export_${new Date().toISOString().split('T')[0]}`, 'Audit Logs');
    toast.success('Audit logs exported to Excel successfully');
  };

  const getActionBadge = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      disable: 'bg-orange-100 text-orange-800',
      enable: 'bg-teal-100 text-teal-800',
      activate: 'bg-green-100 text-green-800',
      deactivate: 'bg-gray-100 text-gray-800',
      status_change: 'bg-purple-100 text-purple-800',
      assign: 'bg-indigo-100 text-indigo-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[action] || 'bg-gray-100 text-gray-800'}`}>
        {action.toUpperCase()}
      </span>
    );
  };

  const getEntityBadge = (entityType) => {
    const colors = {
      user: 'bg-blue-100 text-blue-800',
      company: 'bg-purple-100 text-purple-800',
      resource: 'bg-green-100 text-green-800',
      project: 'bg-orange-100 text-orange-800',
      reservation: 'bg-indigo-100 text-indigo-800',
      ticket: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[entityType] || 'bg-gray-100 text-gray-800'}`}>
        {entityType.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Admin Audit Trail</h1>
        <p className="text-gray-600 mt-2">Track all administrative actions: create, update, delete operations by admins</p>
        <p className="text-xs text-blue-600 mt-1 bg-blue-50 px-2 py-1 rounded inline-block">
          💡 For user activities (logins, views), see <a href="/admin/user-activity" className="underline font-medium">User Actions Log</a>
        </p>
      </div>

      {/* Advanced Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Admin Email</label>
                <Input
                  placeholder="Search admin email..."
                  value={adminEmailFilter}
                  onChange={(e) => setAdminEmailFilter(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">All Actions</option>
                  <option value="create">Create</option>
                  <option value="update">Update</option>
                  <option value="delete">Delete</option>
                  <option value="disable">Disable</option>
                  <option value="enable">Enable</option>
                  <option value="activate">Activate</option>
                  <option value="deactivate">Deactivate</option>
                  <option value="status_change">Status Change</option>
                  <option value="assign">Assign</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Entity Type</label>
                <select
                  value={entityFilter}
                  onChange={(e) => setEntityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">All Types</option>
                  <option value="user">User</option>
                  <option value="company">Company</option>
                  <option value="resource">Resource</option>
                  <option value="project">Project</option>
                  <option value="reservation">Reservation</option>
                  <option value="ticket">Support Ticket</option>
                </select>
              </div>

              <div className="flex items-end">
                <Button onClick={handleSearch} className="w-full btn-snow">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>

            {/* Row 2 - Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Start Date
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="flex items-end">
                <Button variant="outline" onClick={handleExportExcel} className="w-full">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#62D84E' }}></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No audit logs found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{log.admin_email}</div>
                        {log.ip_address && (
                          <div className="text-xs text-gray-500">{log.ip_address}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getActionBadge(log.action)}
                      </td>
                      <td className="px-6 py-4">
                        {getEntityBadge(log.entity_type)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{log.entity_name || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{log.entity_id.substring(0, 8)}...</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {log.reason || '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {(log.before || log.after) && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={() => setSelectedLog(log)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Audit Log Details</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-6">
                                {/* Log Info */}
                                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                                  <div>
                                    <p className="text-xs text-gray-500">Timestamp</p>
                                    <p className="text-sm font-medium">{new Date(log.timestamp).toLocaleString()}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Admin</p>
                                    <p className="text-sm font-medium">{log.admin_email}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Action</p>
                                    <p className="text-sm font-medium">{log.action.toUpperCase()}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Entity</p>
                                    <p className="text-sm font-medium">{log.entity_type.toUpperCase()} - {log.entity_name || log.entity_id}</p>
                                  </div>
                                </div>

                                {/* Before State */}
                                {log.before && (
                                  <div>
                                    <h3 className="font-semibold text-red-600 mb-2">Before Changes</h3>
                                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                      <pre className="text-xs whitespace-pre-wrap overflow-x-auto">
                                        {JSON.stringify(log.before, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )}

                                {/* After State */}
                                {log.after && (
                                  <div>
                                    <h3 className="font-semibold text-green-600 mb-2">After Changes</h3>
                                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                      <pre className="text-xs whitespace-pre-wrap overflow-x-auto">
                                        {JSON.stringify(log.after, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )}

                                {/* Reason */}
                                {log.reason && (
                                  <div>
                                    <h3 className="font-semibold mb-2">Reason</h3>
                                    <p className="text-sm text-gray-700 p-4 bg-gray-50 rounded-lg">{log.reason}</p>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-6">
        <div className="text-sm text-gray-600">
          Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} logs
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogsPage;